import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, FileDown, Lock, Image as ImageIcon, MapPin, Shirt, User, Film } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, read-only Storyboard page.
 * Route: /share/storyboard/:token  (NOT auth-gated)
 */
export default function PublicStoryboard() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/public/storyboard/${token}`);
      if (res.status === 404) {
        setError('This storyboard link is invalid or has been revoked.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load storyboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    document.documentElement.style.background = '#0A0F1F';
    return () => { document.documentElement.style.background = ''; };
  }, [load]);

  useEffect(() => {
    if (data?.project?.name) {
      document.title = `${data.project.name} · Storyboard`;
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0F1F' }}>
        <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: '#0A0F1F' }}>
        <Film className="w-10 h-10 text-white/30 mb-4" />
        <div className="text-lg text-white/80 font-medium mb-2">Storyboard unavailable</div>
        <div className="text-sm text-white/50 max-w-md">{error}</div>
        <Footer />
      </div>
    );
  }

  if (!data) return null;
  const { project, keyframes, assets, downloads } = data;
  const pdfUrl = `${API}/public/storyboard/${token}/pdf`;
  const aspectClass =
    project.orientation === '9:16' ? 'aspect-[9/16]' :
    project.orientation === '1:1' ? 'aspect-square' : 'aspect-video';

  return (
    <div className="min-h-screen text-white" style={{ background: '#0A0F1F', backgroundImage: 'radial-gradient(ellipse at top, rgba(14, 165, 233, 0.06), transparent 60%)' }} data-testid="public-storyboard-page">
      <Header />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-8 sm:pb-12">
        <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-300/70 mb-3 font-mono">
          Storyboard · {project.orientation} · {project.duration}s
        </div>
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5 tracking-tight" data-testid="public-project-name">
          {project.name}
        </h1>
        {project.logline && (
          <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl" data-testid="public-logline">
            {project.logline}
          </p>
        )}
        {(project.tone || project.genre) && (
          <div className="flex flex-wrap gap-2 mt-5">
            {project.genre && (
              <span className="px-3 py-1 rounded-full text-[11px] font-medium bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                {project.genre}
              </span>
            )}
            {project.tone && (
              <span className="px-3 py-1 rounded-full text-[11px] font-medium bg-violet-500/10 text-violet-300 border border-violet-500/20">
                {project.tone}
              </span>
            )}
          </div>
        )}

        {downloads.storyboard_pdf && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-7 px-5 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-cyan-500 to-cyan-600 text-white hover:from-cyan-600 hover:to-cyan-700 transition-all shadow-lg shadow-cyan-500/20"
            data-testid="public-pdf-download-btn"
          >
            <FileDown className="w-4 h-4" /> Download Storyboard (PDF)
          </a>
        )}
      </section>

      {/* KEYFRAMES */}
      {keyframes.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="public-keyframes-section">
          <SectionHeader
            icon={<ImageIcon className="w-4 h-4" />}
            title="Storyboard"
            subtitle={`${keyframes.length} ${keyframes.length === 1 ? 'frame' : 'frames'}`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {keyframes.map((k, i) => (
              <button
                key={`${k.scene_number}-${k.shot_number}-${i}`}
                onClick={() => setLightbox(k)}
                className="group text-left rounded-xl overflow-hidden border border-white/8 bg-white/[0.02] hover:border-cyan-500/40 transition-all"
                data-testid={`public-keyframe-${i}`}
              >
                <div className={`relative ${aspectClass} bg-black/40 overflow-hidden`}>
                  {k.keyframe_url ? (
                    <img
                      src={k.keyframe_url}
                      alt={`${k.scene_number} ${k.shot_number}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                  {k.locked && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/50 text-emerald-300 text-[10px] font-medium flex items-center gap-1 backdrop-blur-sm">
                      <Lock className="w-3 h-3" /> Locked
                    </div>
                  )}
                </div>
                <div className="px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300/80 font-mono mb-1">
                    {k.scene_number} · {k.shot_number}
                  </div>
                  <div className="text-xs text-white/70 line-clamp-2 leading-snug">
                    {k.description || '—'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ASSET BIBLE */}
      {(assets.cast.length > 0 || assets.costumes.length > 0 || assets.locations.length > 0) && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="public-assets-section">
          <SectionHeader
            icon={<User className="w-4 h-4" />}
            title="Asset Bible"
            subtitle="Cast · Costumes · Locations"
          />

          <AssetGroup
            title="Cast"
            icon={<User className="w-3.5 h-3.5" />}
            items={assets.cast}
            testid="public-cast"
          />
          <AssetGroup
            title="Costumes"
            icon={<Shirt className="w-3.5 h-3.5" />}
            items={assets.costumes}
            testid="public-costumes"
          />
          <AssetGroup
            title="Locations"
            icon={<MapPin className="w-3.5 h-3.5" />}
            items={assets.locations}
            testid="public-locations"
          />
        </section>
      )}

      {keyframes.length === 0 && assets.cast.length === 0 && assets.costumes.length === 0 && assets.locations.length === 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 text-center text-white/50 text-sm">
          The storyboard is still being prepared. Check back soon.
        </section>
      )}

      <Footer />

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setLightbox(null)}
          data-testid="public-lightbox"
        >
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.keyframe_url}
              alt={`${lightbox.scene_number} ${lightbox.shot_number}`}
              className="w-full h-auto rounded-lg max-h-[80vh] object-contain"
            />
            <div className="mt-3 px-1">
              <div className="text-[11px] uppercase tracking-wider text-cyan-300/80 font-mono mb-1">
                {lightbox.scene_number} · {lightbox.shot_number}
              </div>
              <div className="text-sm text-white/80 leading-relaxed">{lightbox.description || '—'}</div>
            </div>
          </div>
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl"
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div className="flex items-center gap-2 text-cyan-300">
        {icon}
        <h2 className="text-base sm:text-lg font-semibold tracking-tight text-white">{title}</h2>
      </div>
      {subtitle && <div className="text-[11px] uppercase tracking-wider text-white/40 font-mono">{subtitle}</div>}
    </div>
  );
}

function AssetGroup({ title, icon, items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50 font-mono mb-3">
        {icon}
        {title}
        <span className="text-white/30">· {items.length}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {items.map((it) => {
          const img = it.url || it.mugshot_url || it.flatlay_url || it.panorama_url;
          return (
            <div
              key={it.id || `${title}-${it.name}`}
              className={`rounded-xl overflow-hidden border bg-white/[0.02] ${
                it.selected ? 'border-cyan-500/40' : 'border-white/8'
              }`}
            >
              <div className="aspect-square bg-black/40 overflow-hidden">
                {img ? (
                  <img src={img} alt={it.name || title} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="px-3 py-2.5">
                <div className="text-xs text-white/85 font-medium truncate">{it.name || 'Untitled'}</div>
                {it.selected && (
                  <div className="text-[10px] text-cyan-300 mt-0.5 font-mono">Selected</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-white/5">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
          <Film className="w-4 h-4 text-white" />
        </div>
        <div className="text-sm font-semibold tracking-tight text-white">eipicenter</div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-10 mt-10 border-t border-white/5">
      <div className="flex flex-wrap items-center justify-between gap-4 text-[11px] text-white/40">
        <div>
          Made with{' '}
          <a
            href="https://eipicenter.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400/80 hover:text-cyan-300 transition-colors"
            data-testid="public-footer-link"
          >
            eipicenter
          </a>
          {' '}— AI Filmmaking Studio
        </div>
        <div className="text-white/30">Read-only · No login required</div>
      </div>
    </footer>
  );
}
